// client/src/utils/matchStatus.js

/**
 * Centralized match and match proposal status constants and helpers.
 *
 * For league-related status constants, see client/src/constants/leagueStatuses.js
 */

// Mirrored bridge of server canonical persisted/domain match statuses.
// Keep import path stable for existing web usage; parity is enforced by scripts/check-constant-parity.js.
const PERSISTED_MATCH_STATUS_VALUES = Object.freeze([
  'created',
  'scheduled',
  'unplayed',
  'normal',
  'home_retired',
  'away_retired',
  'i_retired',
  'opponent_retired',
])

export const MATCH_STATUS_VALUES = PERSISTED_MATCH_STATUS_VALUES

export const MATCH_OUTCOME_STATUS_VALUES = Object.freeze([
  'normal',
  'home_retired',
  'away_retired',
  'i_retired',
  'opponent_retired',
])

export const RETIREMENT_MATCH_STATUS_VALUES = Object.freeze([
  'home_retired',
  'away_retired',
  'i_retired',
  'opponent_retired',
])

// Match status constants
export const MATCH_STATUS = Object.freeze({
  SCHEDULED: PERSISTED_MATCH_STATUS_VALUES[1],
  CREATED: PERSISTED_MATCH_STATUS_VALUES[0],
  UNPLAYED: PERSISTED_MATCH_STATUS_VALUES[2],
  NORMAL: PERSISTED_MATCH_STATUS_VALUES[3],
  HOME_RETIRED: PERSISTED_MATCH_STATUS_VALUES[4],
  AWAY_RETIRED: PERSISTED_MATCH_STATUS_VALUES[5],
  I_RETIRED: PERSISTED_MATCH_STATUS_VALUES[6],
  OPPONENT_RETIRED: PERSISTED_MATCH_STATUS_VALUES[7],
  UNKNOWN: 'unknown',
})

// Match outcomes (completed match statuses)
export const MATCH_OUTCOMES = [
  MATCH_STATUS.NORMAL,
  MATCH_STATUS.HOME_RETIRED,
  MATCH_STATUS.AWAY_RETIRED,
  MATCH_STATUS.I_RETIRED,
  MATCH_STATUS.OPPONENT_RETIRED,
]

export const COMPLETED_STATUSES = new Set(MATCH_OUTCOMES)

// User-facing labels for match statuses
export const MATCH_STATUS_LABELS = Object.freeze({
  [MATCH_STATUS.SCHEDULED]: 'Scheduled',
  [MATCH_STATUS.NORMAL]: 'Completed',
  [MATCH_STATUS.I_RETIRED]: 'You retired/defaulted',
  [MATCH_STATUS.OPPONENT_RETIRED]: 'Opponent retired/defaulted',
  [MATCH_STATUS.HOME_RETIRED]: 'Home player retired/defaulted',
  [MATCH_STATUS.AWAY_RETIRED]: 'Away player retired/defaulted',
  [MATCH_STATUS.UNPLAYED]: 'Not played',
})

/**
 * Get user-friendly label for a match status
 * @param {string} status - Match status
 * @returns {string} User-friendly label
 */
export function getMatchStatusLabel(status) {
  return MATCH_STATUS_LABELS[status] || 'Scheduled'
}

// Match proposal status constants
export const MATCH_PROPOSAL_STATUS = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  SCHEDULED: 'scheduled',
  EXPIRED: 'expired',
  DISMISSED: 'dismissed',
})

const PERSISTED_MATCH_PROPOSAL_STATUS_VALUES = Object.freeze([
  MATCH_PROPOSAL_STATUS.PENDING,
  MATCH_PROPOSAL_STATUS.ACCEPTED,
  MATCH_PROPOSAL_STATUS.DECLINED,
  MATCH_PROPOSAL_STATUS.SCHEDULED,
  MATCH_PROPOSAL_STATUS.EXPIRED,
  MATCH_PROPOSAL_STATUS.DISMISSED,
])

export const MATCH_PROPOSAL_STATUSES = PERSISTED_MATCH_PROPOSAL_STATUS_VALUES

// Inactive proposal statuses that should not be shown to users
// These proposals have been resolved and no action can be taken
export const INACTIVE_PROPOSAL_STATUSES = Object.freeze(
  PERSISTED_MATCH_PROPOSAL_STATUS_VALUES.filter(
    (status) => status !== MATCH_PROPOSAL_STATUS.PENDING,
  ),
)

// User-facing labels for match proposal statuses
export const MATCH_PROPOSAL_STATUS_LABELS = Object.freeze({
  [MATCH_PROPOSAL_STATUS.PENDING]: 'Pending Response',
  [MATCH_PROPOSAL_STATUS.ACCEPTED]: 'Accepted',
  [MATCH_PROPOSAL_STATUS.DECLINED]: 'Declined',
  [MATCH_PROPOSAL_STATUS.SCHEDULED]: 'Scheduled',
  [MATCH_PROPOSAL_STATUS.EXPIRED]: 'Expired',
  [MATCH_PROPOSAL_STATUS.DISMISSED]: 'Dismissed',
})

/**
 * Check if a match status indicates the match is completed
 * @param {string} status - Match status to check
 * @returns {boolean} True if the match is completed
 */
export function isCompletedStatus(status) {
  return COMPLETED_STATUSES.has(status)
}

/**
 * Check if a match status indicates a retirement
 * @param {string} status - Match status to check
 * @returns {boolean} True if the match ended in retirement
 */
export function isRetirementStatus(status) {
  return RETIREMENT_MATCH_STATUS_VALUES.includes(status)
}

/**
 * Check if a proposal is active (can be acted upon)
 * @param {Object} proposal - Proposal object with status property
 * @returns {boolean} True if the proposal is active and can be interacted with
 */
export function isActiveProposal(proposal) {
  if (!proposal || !proposal.status) {
    return false
  }
  return !INACTIVE_PROPOSAL_STATUSES.includes(proposal.status)
}
